<template>
  <div>
    <section class="p-box">
      <header v-if="!isDocomoPlan" class="p-box__header">
        <div class="p-filtersA">
          <div v-if="bulkUpdateModes.length !== 0" class="p-filtersA__left">
            <template v-if="canDisplayChangeUi && showMoreThanEditorAuthority">
              <ul class="c-checkbox">
                <li v-show="!stores_loading" class="c-checkbox__item">
                  <input
                    id="selectAll"
                    :checked="isSelectAll"
                    type="checkbox"
                    class="c-checkbox__input"
                    @change="toggleSelectAll"
                  >
                  <label class="c-checkbox__label u-fsS" for="selectAll">
                    {{ $t('labels.selectAll') }}
                  </label>
                </li>
              </ul>
              <div v-show="!isStoreIdsEmpty" class="c-select">
                <select v-model="selectedBulkUpdateMode">
                  <option :value="null" disabled>{{ $t('pages.CompaniesLocationStores.bulkUpdate') }}</option>
                  <option v-for="(mode, i) in bulkUpdateModes" :key="i" :value="mode">
                    {{ $t('pages.CompaniesLocationStores.bulkUpdateMode.' + mode) }}
                  </option>
                </select>
              </div>
            </template>
          </div>
          <div class="p-filtersA__right">
            <div v-if="!(stores_loading || searchAttributesLoading)" class="c-buttonFilter" @click="openModal">
              {{ $t('pages.CompaniesReviews.filters') }}
              <div class="c-buttonFilter__icon">
                <Icon name="iconFilter" />
              </div>
            </div>
            <div class="sortForm c-buttonSort">
              <button
                class="c-buttonSort__item"
                :class="`is-${sortKey === 'code' && sortOrder}`"
                type="button"
                :disabled="stores_loading || searchAttributesLoading"
                @click="sort('code')"
                v-text="$t('pages.CompaniesLocationStores.sortStoreCode')"
              />
            </div>
            <div class="c-searchField">
              <input
                v-model="searchQuery"
                type="text"
                class="searchForm"
                :placeholder="$t('labels.locationOrStoreCodeSearch')"
                @keydown.enter="search"
              >
              <Icon name="iconSearch" />
            </div>
          </div>
        </div>
        <div v-show="!stores_loading && isSelectAllPageStores" class="select-all-option p-filtersA">
          <p class="select-all-option__text">
            {{ currentSelectedText }}
          </p>
          <button type="button"
                  class="c-buttonFilter"
                  @click="toggleSelectAllPageStores"
          >
            {{ currentSelectedButtonText }}
          </button>
          <p class="select-description">{{ $t('pages.CompaniesLocationStores.selectDescription') }}</p>
        </div>
      </header>
      <div class="p-box__body">
        <div v-if="!isDocomoPlan" class="p-box__info">
          <div class="c-info">
            <div class="c-info__icon">
              <Icon name="iconQuestion" />
            </div>
            <div class="c-info__content">
              <p>{{ $t('models.gmb.protection.description1') }}</p>
              <p>{{ $t('models.gmb.protection.description2') }}</p>
            </div>
          </div>
        </div>
        <Loading v-show="!storesMeta || stores_loading" />
        <template v-if="storesMeta && !stores_loading">
          <div v-if="stores.length">
            <div class="p-stores">
              <div
                v-for="(store, i) in stores"
                :key="i"
                class="p-stores__item"
              >
                <!-- チェックボックス -->
                <div v-if="canDisplayChangeUi && showMoreThanEditorAuthority && bulkUpdateModes.length !== 0" class="edit-checkbox">
                  <ul class="c-checkbox">
                    <li class="c-checkbox__item">
                      <input
                        :id="'store' + store.id"
                        :checked="selectedStores.includes(store.id)"
                        type="checkbox"
                        class="c-checkbox__input"
                        @change="setSelectedStores(store.id)"
                      >
                      <label :for="'store' + store.id" class="c-checkbox__label u-fsS" />
                    </li>
                  </ul>
                </div>
                <div v-else class="edit-checkbox edit-checkbox__empty">
                  <ul class="c-checkbox">
                    <li class="c-checkbox__item">
                      <input type="checkbox" class="c-checkbox__input">
                      <label class="c-checkbox__label u-fsS" />
                    </li>
                  </ul>
                </div>
                <!-- ブランドアイコン -->
                <div v-if="!isDocomoPlan" class="brand__wrapper">
                  <div class="p-stores__brand__icon">
                    <img :src="store.attributes.brand_image_url" alt="">
                  </div>
                </div>
                <!-- 店舗概要 -->
                <div class="summary__wrapper">
                  <p class="p-stores__name">{{ store.attributes.name }}</p>
                  <ul class="p-stores__detail">
                    <li>{{ store.attributes.postal_code }}</li>
                    <li>{{ store.attributes.full_address }}</li>
                    <li>
                      {{ phones(store.attributes.primary_phone, store.attributes.additional_phones) }}
                    </li>
                  </ul>
                  <p v-if="!isDocomoPlan" class="p-stores__code">{{ $t('models.store.code') }}：{{ truncateName(store.attributes.code) }}</p>
                  <p class="p-stores__status">
                    <span v-if="openInfoStatus(store)"
                          :class="`statusMark__${openInfoStatus(store)}`"
                          class="statusMark"
                    />
                    {{ displayStoreGmbOpenStatus(openInfoStatus(store)) }}
                  </p>
                  <p v-if="['closed_temporarily', 'closed_permanently'].includes(store.attributes.open_info.status)" class="statusCaution">
                    {{ $t('pages.CompaniesLocationStore.openInfoStatusCaution', { openInfoStatus: displayStoreGmbOpenStatus(openInfoStatus(store)) }) }}
                  </p>
                </div>
                <!-- 連携サイト -->
                <div class="sites__wrapper">
                  <p class="sites__label">{{ $t('labels.connectedSites') }}</p>
                  <ul v-if="hasConnectedSites(store)" class="sites__group">
                    <li
                      v-for="( site, j ) in store.attributes.sites"
                      :key="j"
                      class="sites__item"
                    >
                      <div v-if="isSiteTypeGmb(site)">
                        <span v-if="store.isConnectedGmb">
                          <KkSiteIcon :name="site.site_type" />
                        </span>
                      </div>
                      <div v-else>
                        <span v-if="!!site.url">
                          <KkSiteIcon :name="site.site_type" />
                        </span>
                      </div>
                    </li>
                    <li v-if="store.attributes.shufoo_connected" class="sites__item">
                      <span>
                        <KkSiteIcon name="shufoo" />
                      </span>
                    </li>
                    <li v-if="store.attributes.mapion_biz_connected" class="sites__item">
                      <span>
                        <KkSiteIcon name="mapion_biz" />
                      </span>
                    </li>
                    <li v-if="store.attributes.docomo_connected" class="sites__item">
                      <span>
                        <KkSiteIcon name="docomo" />
                      </span>
                    </li>
                  </ul>
                  <p v-else class="sites__warn">{{ $t('messages.noConnectedSites') }}</p>
                </div>
                <!-- 営業時間 -->
                <div class="hours__wrapper">
                  <div>
                    <p class="hours__label">{{ $t('models.storeRegularHour.modelName') }}</p>
                    <BusinessHoursCrawlerStatus v-if="providedFeaturesBusinessHoursCrawler" :status="store.attributes.business_hours_crawler_status" />
                    <ol v-if="store.attributes.regularHours.length !== 0">
                      <li v-for="weekday in weeks" :key="weekday" class="hours__list">
                        <p class="hours__wday">{{ displayWeekday(weekday) }}</p>
                        <span>
                          {{ displayHour('regularHour')(regularHour(store.attributes.regularHours, weekday)) }}
                        </span>
                      </li>
                    </ol>
                  </div>
                </div>
                <!-- 編集/削除 -->
                <div class="actions__wrapper">
                  <div>
                    <router-link :to="{ name: 'CompaniesLocationStore', params: { company_uuid: companyUuid, store_uuid: store.attributes.uuid }}" class="p-stores__action__item">
                      <Icon name="iconShow" class="p-stores__action__icon" />
                      {{ $t('pages.CompaniesLocationStores.showDetail') }}
                    </router-link>
                    <div
                      v-if="showMoreThanEditorAuthority && !isDocomoPlan"
                      class="deleteButton p-stores__action__item"
                      @click="submitDeleteStore(store.attributes.uuid)"
                    >
                      <img :src="iconCrossPath" alt="" class="p-stores__action__icon">
                      {{ $t('labels.delete') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else v-text="$t('messages.noData')" />
          <div v-if="totalPages" class="p-controller">
            <div class="p-controller__count">
              <p class="p-controller__count__name">{{ $t('labels.perPageCount') }}</p>
              <div class="p-controller__count__select">
                <select :value="displayNumber" @change="changeDisplayNumber($event)">
                  <option v-for="item in 5" :key="item" :value="item * 50">{{ item * 50 }}</option>
                </select>
              </div>
              <p class="p-controller__count__current">{{ startDataNumber }}-{{ endDataNumber }}</p>
            </div>
            <div class="p-controller__navi">
              <button
                type="button"
                :disabled="stores_loading || currentPage === 1"
                class="p-controller__navi__prev"
                @click="goPrevPage"
              >
                <Icon name="arrowLeft" />
              </button>
              <button
                type="button"
                :disabled="stores_loading || currentPage >= totalPages"
                class="p-controller__navi__next"
                @click="goNextPage"
              >
                <Icon name="arrowRight" />
              </button>
            </div>
          </div>
          <div v-if="showMoreThanEditorAuthority" class="c-button c-button--right">
            <div>
              {{ storesMeta.store_count }} / {{ storesMeta.max_store_count }} {{ $t('labels.registeredStoresCount') }}
            </div>
            <div v-if="canAddStores()">
              <template v-if="showOnlyOwnerAuthority && !isDocomoPlan">
                <router-link :to="{ name: 'CompaniesLocationStoreNew', params: { company_uuid: companyUuid }}" class="c-button__item c-button__item--primary">
                  <img :src="iconPlusPath" alt="" class="c-button__icon">
                  {{ $t('labels.registerNewStore') }}
                </router-link>
              </template>
            </div>
            <div v-else>
              <router-link v-if="!isDocomoPlan" :to="{ name: 'PaymentSetting', params: { company_uuid: companyUuid }}" class="c-button__item c-button__item--primary">
                <img :src="iconPlusPath" alt="" class="c-button__icon">
                {{ $t('labels.upgradePlanAndRegisterNewStore') }}
              </router-link>
            </div>
          </div>
        </template>
      </div>
    </section>
    <KkFilterModal
      :is-visible="modal"
      :filter-items="filterItems"
      :form-values="formValues"
      :total-count="filteredCount"
      @close="modal = false"
      @submit="handleSubmit"
      @reset="handleReset"
      @change-check-box="handleChangeFilterModalCheckBox"
    />
  </div>
</template>

<script>
import {mapState, mapActions, mapMutations, mapGetters} from 'vuex'
import cloneDeep from 'lodash.clonedeep'
import Loading from '../components/Loading.vue'
import Icon from '../components/Icon.vue'
import { KkSiteIcon } from '../components/base'
import BusinessHoursCrawlerStatus from '../components/CompaniesLocationStore/BusinessHoursCrawlerStatus.vue'
import iconPlusPath from '../../../images/common/icon_plus.svg'
import iconCrossPath from '../../../images/common/icon_cross.svg'
import { KkFilterModal } from '../components/projects'
import { camelCase } from '../modules/string_converter'
import {addAttributes} from '../modules/filterModalConst'
import { displayHour, week } from '../modules/regularHour'
import { actionEvent } from 'src/biz_spa/modules/googleAnalytics'

export default {
  name: 'CompaniesLocationStores',
  components: {
    Loading,
    Icon,
    KkSiteIcon,
    KkFilterModal,
    BusinessHoursCrawlerStatus
  },
  beforeRouteEnter(to, form, next) {
    if (to.query.per === undefined) {
      next(vm => vm.setDisplayNumber(vm.displayNumber))
    } else {
      next()
    }
  },
  beforeRouteUpdate (to, _, next) {
    if (!Object.keys(to.query).length) {
      this.initSearch()
    }
    next()
    if (!this.hasInitFilter) {
      this.readStores(this.readStoresParams)
    }
  },
  data () {
    return {
      companyUuid: this.$route.params.company_uuid,
      searchQuery: this.$route.query['q[name_cont]'] || '',
      currentPage: Number(this.$route.query['page']) || 1,
      modal: false,
      iconPlusPath,
      iconCrossPath,
      selectedStores: [],
      selectedBulkUpdateMode: null,
      filterItems: [],
      sortKey: null,
      sortOrder: null,
    }
  },
  computed: {
    ...mapState({
      company: state => state.companiesCompany.data,
      rawStores: state => state.companiesLocationStores.data,
      storesMeta: state => state.companiesLocationStores.meta,
      filteredCount: state => state.companiesLocationStores.filteredCount,
      allGmbStores: (state) => state.companiesGmbStores.data ? state.companiesGmbStores.data.data : [], // 全ての（全ページの）GMB連携済の店舗
      stores_loading: state => state.companiesLocationStores.loading,
      formValues: state => state.companiesSearchTagGroups.formValues,
      searchAttributesLoading: state => state.companiesSearchTagGroups.loading,
      searchAttributes: state => state.companiesSearchTagGroups.data,
      sites: state => state.companiesSearchTagGroups.data.sites,
      brands: state => state.companiesSearchTagGroups.data.brands,
      franchisees: state => state.companiesSearchTagGroups.data.franchisees,
      areas: state => state.companiesSearchTagGroups.data.areas,
      user_companies: state => state.companiesSearchTagGroups.data.user_companies,
      prefectures : state => state.companiesSearchTagGroups.data.prefectures,
      original_tag_group_a : state => state.companiesSearchTagGroups.data.original_tag_group_a,
      original_tag_group_b : state => state.companiesSearchTagGroups.data.original_tag_group_b,
      original_tag_group_c : state => state.companiesSearchTagGroups.data.original_tag_group_c,
      original_tag_group_d : state => state.companiesSearchTagGroups.data.original_tag_group_d,
      original_tag_group_e : state => state.companiesSearchTagGroups.data.original_tag_group_e,
      displayNumber: state => state.filters.displayNumber,
    }),
    ...mapGetters({
      canDisplayChangeUi: 'companiesCompany/canDisplayChangeUi',
      providedFeaturesBusinessHoursCrawler: 'companiesCompany/providedFeaturesBusinessHoursCrawler',
      providedFeaturesBulkUpdate: 'companiesCompany/providedFeaturesBulkUpdate',
      providedFeaturesGmb: 'companiesCompany/providedFeaturesGmb',
      providedFeaturesBulkUpdateStoreOption: 'companiesCompany/providedFeaturesBulkUpdateStoreOption',
      showMoreThanEditorAuthority: 'companiesAccessibleApi/showMoreThanEditorAuthority',
      showOnlyOwnerAuthority: 'companiesAccessibleApi/showOnlyOwnerAuthority',
      isDocomoPlan: 'companiesCompany/isDocomoPlan',
    }),
    weeks() {
      return week
    },
    filterKey() {
      return this.$route.query.filterKey
    },
    filterValue() {
      return this.$route.query.filterValue
    },
    hasInitFilter() {
      return this.filterKey && this.filterValue
    },
    totalPages() {
      return this.storesMeta?.total_pages
    },
    isStoreIdsEmpty() {
      return !this.selectedStores.length
    },
    isSelectAll() {
      return this.storesMeta?.allStoreIds?.every(v => this.selectedStores.includes(v))
    },
    isSelectAllPageStores() {
      return this.rawStores.map(v => v.id).every(v => this.selectedStores.includes(v))
    },
    currentSelectedText() {
      if (this.isSelectAll) {
        return this.$t('pages.CompaniesLocationStores.allPageSelected', {count: this.selectedStores.length })
      } else {
        return this.$t('pages.CompaniesLocationStores.thisPageSelected', { count: this.selectedStores.length})
      }
    },
    currentSelectedButtonText() {
      if (this.isSelectAll) {
        return this.$t('pages.CompaniesLocationStores.reset')
      } else {
        return this.$t('pages.CompaniesLocationStores.allPageSelect', { count : this.storesMeta?.allStoreIds?.length })
      }
    },
    startDataNumber: function () {
      return (this.currentPage - 1) * this.displayNumber + 1
    },
    endDataNumber: function () {
      return this.startDataNumber + this.stores.length - 1
    },
    stores() {
      return this.rawStores.map(store => {
        store.id = Number(store.id) // なぜかstringになるのでNumberをキャスト
        store.isConnectedGmb = this.allGmbStores.find(gmbStore => Number(gmbStore.id) === store.id) !== undefined
        return store
      })
    },
    bulkUpdateModes() {
      const modes = []
      if(this.providedFeaturesBulkUpdate) modes.push('bulkEdit')
      if(this.providedFeaturesGmb) {
        modes.push('bulkUpdateInGmb')
        modes.push('bulkUpdateWithGmb')
      }
      if(this.providedFeaturesBulkUpdateStoreOption) modes.push('bulkEditStoreOptions')
      return modes
    },
    selectedStoreIncludeHotelBrand() {
      return this.allGmbStores.some(store => this.selectedStores.includes(Number(store.id)) && store.attributes.hotel_brand)
    },
    selectedStoreIncludeFoodBrand() {
      return this.allGmbStores.some(store => this.selectedStores.includes(Number(store.id)) && store.attributes.food_brand)
    },
    readStoresParams() {
      const { brands, areas, franchisees, user_companies, prefectures, sites } = this.formValues
      return {
        companyUuid: this.companyUuid,
        params: {
          per: this.displayNumber,
          page: this.currentPage,
          q:{
            name_cont: this.searchQuery || undefined,
            option_code_cont: this.searchQuery || undefined,
            m: 'or',
          },
          search: {
            brand_ids: (brands && brands.length === 0) ? undefined : brands,
            area_ids: areas === 0 ? undefined : areas,
            franchisee_ids: franchisees === 0 ? undefined : franchisees,
            user_company_ids: user_companies === 0 ? undefined : user_companies,
            prefectures: prefectures === 0 ? undefined : prefectures,
            site_types: sites === 0 ? undefined : sites,
            original_tag_group_a_tag_ids: this.formValues.original_tag_group_a,
            original_tag_group_b_tag_ids: this.formValues.original_tag_group_b,
            original_tag_group_c_tag_ids: this.formValues.original_tag_group_c,
            original_tag_group_d_tag_ids: this.formValues.original_tag_group_d,
            original_tag_group_e_tag_ids: this.formValues.original_tag_group_e,
            sort_key: this.sortKey,
            sort_order: this.sortOrder,
          },
        }
      }
    },
  },
  watch: {
    selectedBulkUpdateMode(mode) {
      switch(mode) {
      case 'bulkEdit':
        this.setStoreIds(this.selectedStores.filter(v => this.allGmbStores.map(v => Number(v.id)).includes(v)))
        this.$router.push({ name: 'CompaniesLocationStoresBulkEdit', params: { storeIds: this.selectedStores, selectedStoreIncludeHotelBrand: this.selectedStoreIncludeHotelBrand , selectedStoreIncludeFoodBrand: this.selectedStoreIncludeFoodBrand } })
        break
      case 'bulkUpdateWithGmb':
        this.setStoreIds(this.selectedStores.filter(v => this.allGmbStores.map(v => Number(v.id)).includes(v)))
        this.$router.push({ name: 'CompaniesLocationStoresBulkUpdateWithGmb', params: { storeIds: this.selectedStores } })
        break
      case 'bulkUpdateInGmb':
        this.setStoreIds(this.selectedStores.filter(v => this.allGmbStores.map(v => Number(v.id)).includes(v)))
        this.$router.push({ name: 'CompaniesLocationStoresBulkUpdateInGmb', params: { storeIds: this.selectedStores } })
        break
      case 'bulkEditStoreOptions':
        this.$router.push({ name: 'CompaniesLocationStoresBulkEditStoreOptions', params: { storeIds: this.selectedStores } })
        break
      }
      actionEvent('BulkOps')
    }
  },
  async created() {
    this.initStoresState()
    this.readGmbStores(this.companyUuid)
    await this.readSearchTagGroups()
    if (this.hasInitFilter) {
      const formValues = cloneDeep(this.formInitialValues())
      formValues[this.filterKey] = [Number(this.filterValue)]
      this.setFormValues(formValues)
    } else {
      this.setFormValues(this.formInitialValues())
    }
    this.createFilter()
    this.readStores(this.readStoresParams)
  },
  methods: {
    ...mapActions({
      readSearchTagGroups: 'companiesSearchTagGroups/readSearchTagGroups',
      initStoresState: 'companiesLocationStores/initStoresState',
      readStores: 'companiesLocationStores/readStores',
      readStoresForFilter: 'companiesLocationStores/readStoresForFilter',
      readGmbStores: 'companiesGmbStores/readGmbStores',
      deleteStore: 'companiesLocationStores/deleteStore',
      setDisplayNumber: 'filters/setDisplayNumber',
    }),
    ...mapMutations({
      setFormValues: 'companiesSearchTagGroups/setFormValues',
    }),
    displayHour,
    truncateName(name) {
      if (!name) {
        return '-'
      } else if (name.length > 15) {
        return name.slice(0, 15) + '...'
      } else {
        return name
      }
    },
    setStoreId(id) {
      if (this.selectedStores.includes(id)) {
        this.selectedStores = this.selectedStores.filter(v => v !== id)
      } else {
        this.selectedStores.push(id)
      }
    },
    setStoreIds(storeIds) {
      this.selectedStores = storeIds
    },
    setSelectedStores(val) {
      this.setStoreId(val)
    },
    toggleSelectAllPageStores() {
      if (this.isSelectAllPageStores) {
        this.toggleSelectAll()
      } else {
        this.setStoreIds(this.storesMeta.allStoreIds)
      }
    },
    regularHour(regularHours, weekday) {
      return regularHours.find(regularHour => regularHour.wday === weekday)
    },
    displayWeekday(weekday) {
      return this.$t('labels.shortWeekday.' + weekday)
    },
    formInitialValues() {
      return {
        sites: [],
        brands: [],
        franchisees: [],
        areas: [],
        user_companies: [],
        prefectures: [],
        original_tag_group_a: [],
        original_tag_group_b: [],
        original_tag_group_c: [],
        original_tag_group_d: [],
        original_tag_group_e: [],
      }
    },
    initSearch: function () {
      this.searchQuery = ''
      this.currentPage = 1
    },
    goPrevPage() {
      --this.currentPage
      this.changePage()
    },
    goNextPage() {
      ++this.currentPage
      this.changePage()
    },
    changePage() {
      window.scrollTo({ top: 0 })
      this.readStores(this.readStoresParams)
    },
    /**
     * 表示件数の変更
     * @param  { Object } e イベントオブジェクト
     * @return { Void }
     */
    changeDisplayNumber(e) {
      this.setDisplayNumber(e.target.value)
      this.reloadAndQuerySubmit()
    },
    sort(key) {
      if (this.loading) return

      if (this.sortKey === key) {
        this.sortOrder = (this.sortOrder === 'desc' ) ? 'asc' : 'desc'
      } else {
        this.sortKey = key
        this.sortOrder = 'desc'
      }
      this.reloadAndQuerySubmit()
    },
    search: function (event) {
      if (event.keyCode !== 13) return
      if (this.searchQuery === this.$route.query['q[name_cont]']) return

      this.reloadAndQuerySubmit()
    },
    reloadAndQuerySubmit() {
      this.resetPage()
      this.readStores(this.readStoresParams)
    },
    hasConnectedSites(store) {
      if(store.attributes.shufoo_connected || store.attributes.mapion_biz_connected || store.attributes.docomo_connected) return true
      return store.attributes.sites.filter(site => !!site?.url).length !== 0 || store.isConnectedGmb
    },
    resetPage: function () {
      this.currentPage = 1
      this.$route.query.page = undefined
    },
    canAddStores: function () {
      return this.storesMeta.max_store_count > this.storesMeta?.allStoreIds?.length
    },
    async submitDeleteStore(uuid) {
      if (confirm(this.$t('pages.CompaniesLocationStores.confirmToDelete'))) {
        await this.deleteStore({
          companyUuid: this.companyUuid,
          uuid: uuid,
        })
        this.readStores(this.readStoresParams)
      }
    },
    openModal() {
      this.modal = true
    },
    handleSubmit() {
      this.reloadAndQuerySubmit()
      this.modal = false
      actionEvent('StoreFilter', JSON.stringify(this.readStoresParams))
    },
    handleReset() {
      this.setFormValues(this.formInitialValues())
      this.reloadAndQuerySubmit()
      this.modal = false
    },
    toggleSelectAll() {
      if (this.isSelectAll) {
        this.setStoreIds([])
      } else {
        this.setStoreIds(this.storesMeta.allStoreIds)
      }
      actionEvent('SelectStore')
    },
    handleChangeFilterModalCheckBox({ kind, id }) {
      const newFormValues = Object.assign({}, this.formValues)

      if(newFormValues[kind].includes(id)) {
        newFormValues[kind] = newFormValues[kind].filter((_id) => _id !== id)
      } else {
        newFormValues[kind].push(id)
      }

      this.setFormValues(newFormValues)
      this.readStoresForFilter(this.readStoresParams)
    },
    phones(primaryPhone, additionalPhones = []) {
      if (!primaryPhone.number) return
      let phones = primaryPhone.number
      if (additionalPhones.length) phones = phones +  ' | '
      return phones + additionalPhones.map(field => field.number).join(' | ')
    },
    openInfoStatus(store) {
      if(store.attributes.open_info.status) return camelCase(store.attributes.open_info.status)
    },
    displayStoreGmbOpenStatus(status) {
      if (status) {
        return this.$t('pages.CompaniesLocationStore.openInfoStatus.' + status)
      }
    },
    isSiteTypeGmb(site) {
      return site.site_type === 'google'
    },
    createFilter() {
      const attributes = [
        this.searchAttributes.brands,
        this.searchAttributes.areas,
        this.searchAttributes.franchisees,
        this.searchAttributes.user_companies,
      ]

      if(!this.isDocomoPlan) attributes.unshift(this.searchAttributes.sites)
      if(this.searchAttributes.original_tag_group_a) attributes.push(this.searchAttributes.original_tag_group_a)
      if(this.searchAttributes.original_tag_group_b) attributes.push(this.searchAttributes.original_tag_group_b)
      if(this.searchAttributes.original_tag_group_c) attributes.push(this.searchAttributes.original_tag_group_c)
      if(this.searchAttributes.original_tag_group_d) attributes.push(this.searchAttributes.original_tag_group_d)
      if(this.searchAttributes.original_tag_group_e) attributes.push(this.searchAttributes.original_tag_group_e)

      attributes.push(this.searchAttributes.prefectures)
      this.filterItems = attributes.map((attr) => {
        if(attr && attr.kind){
          return addAttributes(attr, attr.kind)
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../../stylesheets/biz_spa/foundation/_var.scss";
@import "../../../stylesheets/biz_spa/foundation/_mixin.scss";
.searchForm {
  width: 220px;
}
.deleteButton {
  cursor: pointer;
}
.edit-checkbox {
  margin-top: auto;
  margin-bottom: auto;
  padding-right: 24px;
  &__empty {
    visibility: hidden;
  }
}
.brand {
  &__wrapper {
    flex-basis: 100%;
    @include mq() {
      flex-basis: 104px;
      padding-right: 24px;
    }
  }
}
.summary {
  &__wrapper {
    flex-basis: 100%;
    margin-top: 24px;
    @include mq() {
      flex-basis: 220px;
      min-width: 220px;
      margin-top: 0;
      padding-right: 24px;
    }
    @include mq(xl) {
      flex-basis: 304px;
      min-width: 304px;
    }
  }
}
.statusCaution {
  margin-top: 8px;
  color: $color-accent-primary;
  font-size: 12px;
  line-height: 1.2;
  letter-spacing: .15px;
}
.sites {
  &__wrapper {
    flex-basis: 100%;
    margin-top: 24px;
    @include mq() {
      flex-basis: 156px;
      min-width: 156px;
      margin-top: 0;
      padding-right: 24px;
    }
    @include mq(xl) {
      flex-basis: 270px;
      min-width: 270px;
    }
  }
  &__group {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -4px -12px;
  }
  &__label {
    margin-bottom: 4px;
    font-weight: bold;
  }
  &__warn {
    margin-top: 12px;
    color: #E9446A;
    font-weight: bold;
  }
  &__item {
    padding: 0 2px 10px;
    span {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 38px;
      height: 38px;
      padding: 4px;
      border: 1px solid rgba(228, 231, 235, .5);
      border-radius: 8px;
      background-color: #FFF;
    }
    img {
      display: block;
    }
  }
}
.hours {
  &__wrapper {
    display: none;
    flex-basis: 100%;
    margin-top: 24px;
    @include mq(md) {
      display: block;
      min-width: 184px;
      margin-top: 0;
      padding-right: 24px;
    }
  }
  &__label {
    margin-bottom: 4px;
    font-weight: bold;
  }
  &__list {
    display: flex;
  }
  &__period {
    display: flex;
  }
  &__wday {
    margin-right: 16px;
  }
  &__hyphen {
    margin-right: 8px;
    margin-left: 8px;
  }
}
.actions {
  &__wrapper {
    display: flex;
    justify-content: flex-end;
    align-self: center;
    margin-top: 24px;
    margin-left: auto;
    white-space: nowrap;
    @include mq() {
      margin-top: 0;
    }
  }
}
.p-filtersA {
  @include mq() {
    justify-content: initial;
  }
  .p-filtersA__right {
    margin-left: auto;
    .c-buttonFilter {
      margin-right: 16px;
    }
    .c-buttonSort {
      margin-right: 16px;
      margin-left: 0;
    }
  }
}
.select-all-option {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 8px 0;
  background: #F5F6F9;
  &__text {
    margin-right: 16px;
  }
}
.select-description {
  width: 100%;
  color: $color-accent-primary;
  text-align: center;
}
.statusMark {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  &__open {
    background: $complete-color;
  }
  &__closedTemporarily {
    background: $color-accent-primary;
  }
  &__closedPermanently {
    background: #D2D5DB;
  }
}
</style>
